import { useLocation, type Location } from 'react-router-dom';

// Vite resolves this to a hashed asset URL at build time; in tests the import
// returns a string that jsdom renders as-is — we never actually decode the
// bytes, we just put the URL on an <img>.
import dragonLogoUrl from '../assets/dragon-is-logo.jpg';

/**
 * `/login` page (Entra SSO).
 *
 * A standalone, centered card built from native HTML + Tailwind primitives
 * (no UI library — the surface is small and WCAG-AA is easier to reason about
 * on primitives). There is no username/password form: under Entra SSO the
 * only sign-in affordance is a single full-page browser navigation to the
 * server's `/api/v1/auth/login` endpoint.
 *
 * Sign-in path: a plain `<a href>` (NOT `apiFetch`/`fetch`, NOT react-router
 * navigation). The server responds with a cross-origin 302 to Entra that the
 * browser must follow itself; an XHR/fetch can neither follow that redirect
 * nor surface the IdP login UI, so this MUST be a real document navigation.
 * The original protected destination (`RequireAuth` stashes it in
 * `location.state.from` on its way out) is passed through unchanged as the
 * `returnTo` query param, URL-encoded; it falls back to `/runs`.
 *
 * Error path: on a denied/failed callback the server bounces back to
 * `/login?error=<code>` with `not_authorised` | `disabled` | `bad_state`.
 * We render ONE generic message identical for every code (arch §11 T14): the
 * copy must never reveal whether the email exists in the allowlist, so there
 * is deliberately no per-code branching of the text.
 *
 * No password-reset link by ADR-004: identities now live in Entra.
 */

const LOGIN_ENDPOINT = '/api/v1/auth/login';
const DEFAULT_RETURN_TO = '/runs';

// Identical for every `?error=` code by design (arch §11 T14 — no enumeration
// oracle). Do not branch this text on the code.
const SIGN_IN_ERROR_MESSAGE =
  'Sorry, you could not be signed in. Please contact your administrator.';

/**
 * Narrow `location.state` into the `{from}` shape `RequireAuth` writes, or
 * `null` if anything else is there. React-router types `state` as `unknown`
 * so we guard rather than cast. (Logic preserved verbatim from the pre-SSO
 * page so the post-login destination is derived identically.)
 */
function extractFrom(state: unknown): string | null {
  if (state === null || typeof state !== 'object') return null;
  const candidate = (state as { from?: unknown }).from;
  if (candidate === null || typeof candidate !== 'object') return null;
  const pathname = (candidate as Partial<Location>).pathname;
  const search = (candidate as Partial<Location>).search ?? '';
  const hash = (candidate as Partial<Location>).hash ?? '';
  if (typeof pathname !== 'string' || pathname.length === 0) return null;
  return `${pathname}${search}${hash}`;
}

export function LoginPage(): JSX.Element {
  const location = useLocation();

  const from = extractFrom(location.state) ?? DEFAULT_RETURN_TO;
  const signInHref = `${LOGIN_ENDPOINT}?returnTo=${encodeURIComponent(from)}`;

  // Any recognised `?error=` code maps to the SAME message (T14). We only
  // check membership to avoid showing the banner for unrelated query params;
  // the message itself never varies by code.
  const params = new URLSearchParams(location.search);
  const errorCode = params.get('error');
  const hasError =
    errorCode === 'not_authorised' || errorCode === 'disabled' || errorCode === 'bad_state';

  // Standalone page: owns the full viewport and renders a centered card on
  // the neutral slate body tone. The Dragon IS portrait mark sits above the
  // Sign-in heading — small, centered, and decorative (`alt=""` so screen
  // readers skip it; the `<h1>` carries the accessible name for this region).
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4 py-12">
      <section
        aria-labelledby="login-heading"
        className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-8 shadow-sm"
      >
        <img
          src={dragonLogoUrl}
          alt=""
          className="mx-auto mb-6 block h-24 w-auto"
          data-testid="login-logo"
        />
        <h1 id="login-heading" className="text-2xl font-semibold text-slate-900 mb-6 text-center">
          Sign in
        </h1>

        {/*
         * `role="alert"` so screen readers announce the message on render.
         * Only rendered when a recognised error code is present: an empty
         * live region still gets announced on some AT.
         */}
        {hasError ? (
          <p role="alert" className="mb-6 text-sm text-red-700">
            {SIGN_IN_ERROR_MESSAGE}
          </p>
        ) : null}

        {/*
         * Sign-in is a full-page document navigation, NOT an `apiFetch`/`fetch`
         * call: the server replies with a cross-origin 302 to Entra that the
         * browser must follow itself. A plain `<a href>` is the simplest thing
         * that does exactly that — no click handler, no JS navigation.
         */}
        <a
          href={signInHref}
          data-testid="sign-in-with-microsoft"
          className="block w-full rounded bg-brand-teal px-4 py-2 text-center font-semibold text-white focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2"
        >
          Sign in with Microsoft
        </a>
      </section>
    </div>
  );
}
